import { Component, Input, OnInit, SimpleChanges, OnDestroy } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-mini-mapa-geojson',
  templateUrl: './mini-mapa-geojson.component.html',
  styleUrls: ['./mini-mapa-geojson.component.css']
})
export class MiniMapaGeojsonComponent implements OnInit, OnDestroy {
  @Input() geojsonData?: any;
  @Input() clienteLatitude?: string | null;
  @Input() clienteLongitude?: string | null;
  
  map!: mapboxgl.Map;
  mapLoaded: boolean = false;
  mapboxAccessToken: string = environment.MAPBOX_TOKEN;
  zoom: number = 12;
  private mapInitialized = false;

  ngOnInit(): void {
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.initializeMap();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.mapInitialized && (changes['clienteLatitude'] || changes['clienteLongitude'])) {
      if (this.clienteLatitude && this.clienteLongitude) {
        this.updateMapCenter();
      }
    }
    
    if (changes['geojsonData'] && this.geojsonData && this.mapLoaded) {
      this.loadGeoJsonOnMap(this.geojsonData);
    }
  }

  private initializeMap() {
    if (this.mapInitialized) return;
    
    (mapboxgl as any).accessToken = this.mapboxAccessToken;
    
    const centerLng = this.clienteLongitude ? parseFloat(this.clienteLongitude) : -49.420580863953;
    const centerLat = this.clienteLatitude ? parseFloat(this.clienteLatitude) : -20.828892200419;
    
    this.map = new mapboxgl.Map({
      container: 'mini-mapa',
      style: 'mapbox://styles/mapbox/satellite-streets-v11?optimize=true',
      center: [centerLng, centerLat],
      zoom: this.zoom,
      attributionControl: false
    });

    this.map.on('load', () => {
      this.mapLoaded = true;
      this.mapInitialized = true;

      if (this.geojsonData) {
        this.loadGeoJsonOnMap(this.geojsonData);
      }
    });
  }

  private updateMapCenter(): void {
    if (!this.mapLoaded || !this.clienteLatitude || !this.clienteLongitude) return;
    
    const lng = parseFloat(this.clienteLongitude);
    const lat = parseFloat(this.clienteLatitude);
    
    if (!isNaN(lng) && !isNaN(lat)) {
      this.map.flyTo({
        center: [lng, lat],
        zoom: this.zoom,
        essential: true
      });
    }
  }

  public loadGeoJsonOnMap(geojson: any): void {
    if (!this.map || !this.mapLoaded) return;

    if (this.map.getSource('geojson-source')) {
      if (this.map.getLayer('geojson-layer')) {
        this.map.removeLayer('geojson-layer');
      }
      if (this.map.getLayer('geojson-outline')) {
        this.map.removeLayer('geojson-outline');
      }
      this.map.removeSource('geojson-source');
    }

    this.map.addSource('geojson-source', {
      type: 'geojson',
      data: geojson
    });

    this.map.addLayer({
      'id': 'geojson-layer',
      'type': 'fill',
      'source': 'geojson-source',
      'paint': {
        'fill-color': 'rgba(109, 231, 34, 0.3)',
        'fill-opacity': 1
      }
    });

    this.map.addLayer({
      'id': 'geojson-outline',
      'type': 'line',
      'source': 'geojson-source',
      'paint': {
        'line-color': '#6DE722',
        'line-width': 3
      }
    });

    if (geojson && geojson.features && geojson.features.length > 0) {
      const firstFeature = geojson.features[0];
      const featureGeometry = firstFeature.geometry;

      if (featureGeometry) {
        const center = this.getCenterOfFeature(featureGeometry);
        if (center) {
          this.map.flyTo({
            center: center,
            essential: true
          });
        }
      }
    }
  }

  private extractCoordinates(feature: any): number[][] {
    if (!feature || !feature.geometry) return [];

    if (feature.geometry.type === 'Polygon') {
      return feature.geometry.coordinates[0];
    }
    if (feature.geometry.type === 'MultiPolygon') {
      return feature.geometry.coordinates.flatMap((polygon: any) => polygon.flatMap((ring: any) => ring));
    }
    return [];
  }

  private getCenterOfFeature(geometry: any): [number, number] | null {
    if (!geometry) return null;

    const coordinates = this.extractCoordinates({ geometry: geometry });

    if (coordinates.length === 0) return null;

    const lons = coordinates.map((coord: number[]) => coord[0]);
    const lats = coordinates.map((coord: number[]) => coord[1]);

    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    const centerLon = (minLon + maxLon) / 2;
    const centerLat = (minLat + maxLat) / 2;

    return [centerLon, centerLat];
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }
}